import { useEffect, useRef, useState } from "react";
import { numero } from "../dados/formatar";

const DURACAO = 1100;
const suavizar = (t: number) => 1 - Math.pow(1 - t, 3);

const semMovimento = () =>
  typeof window === "undefined" ||
  !("IntersectionObserver" in window) ||
  window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

type Props = {
  valor: number;
  prefixo?: string;
  sufixo?: string;
  atraso?: number;
};

/**
 * Conta de 0 até o valor quando entra na tela. O valor final é garantido por
 * um temporizador mesmo se o observador nunca disparar (aba oculta, impressão,
 * captura): um relatório nunca pode ficar mostrando 0 no lugar do número.
 */
export const NumeroAnimado = ({ valor, prefixo = "", sufixo = "", atraso = 0 }: Props) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [atual, setAtual] = useState(semMovimento() ? valor : 0);

  useEffect(() => {
    const alvo = ref.current;
    if (!alvo || semMovimento()) {
      setAtual(valor);
      return undefined;
    }
    let quadro = 0;
    let comecou = false;
    const animar = (inicio: number) => (agora: number) => {
      const progresso = Math.min(1, (agora - inicio) / DURACAO);
      setAtual(Math.round(valor * suavizar(progresso)));
      if (progresso < 1) quadro = requestAnimationFrame(animar(inicio));
    };
    const observador = new IntersectionObserver(
      (entradas) => {
        if (comecou || !entradas.some((entrada) => entrada.isIntersecting)) return;
        comecou = true;
        observador.disconnect();
        window.setTimeout(() => {
          quadro = requestAnimationFrame((agora) => animar(agora)(agora));
        }, atraso * 1000);
      },
      { threshold: 0.2 },
    );
    observador.observe(alvo);
    const garantia = window.setTimeout(() => {
      cancelAnimationFrame(quadro);
      setAtual(valor);
    }, atraso * 1000 + DURACAO + 1500);
    return () => {
      observador.disconnect();
      cancelAnimationFrame(quadro);
      window.clearTimeout(garantia);
    };
  }, [valor, atraso]);

  return (
    <span ref={ref} aria-label={`${prefixo}${numero(valor)}${sufixo}`}>
      {`${prefixo}${numero(atual)}${sufixo}`}
    </span>
  );
};
